import React from "react";
import { useTranslation } from "react-i18next";
import AppHeader from "../common/AppHeader";
import styles from "../css/LegalPage.module.css";

const policyContent = {
  en: {
    title: "Privacy Policy",
    updated: "Last updated: March 31, 2026",
    intro:
      "This Privacy Policy explains how Bhada24 handles service provider information on the platform. Our platform is designed to help service providers list their services, reach more users, and grow their business.",
    sections: [
      {
        heading: "1. Information We Collect",
        points: [
          "We may collect service provider details such as name, phone number, email address, profile image, business-related information, service listings, and other details submitted while using the platform.",
          "We may also collect usage and technical information required for login, notifications, and platform security.",
        ],
      },
      {
        heading: "2. How We Use Information",
        points: [
          "We use service provider information to create and manage listings, support account access, handle enquiries, improve platform operations, and help providers connect with potential customers.",
          "We may use provider contact details for account communication, customer enquiries, support, service coordination, and business-related communication on the platform.",
        ],
      },
      {
        heading: "3. Sharing of Contact Details",
        points: [
          "We may share a service provider's contact details with users only for legitimate business purposes, such as enabling customers to contact the provider about listed services, bookings, availability, pricing, or related business discussions.",
          "We do not intend provider contact information to be used for unrelated marketing, spam, or any purpose outside genuine service-related communication.",
        ],
      },
      {
        heading: "4. Data Protection",
        points: [
          "We take reasonable steps to protect personal and business information stored on the platform. However, no online system can guarantee absolute security.",
          "Service providers are responsible for keeping their account credentials and device access secure.",
        ],
      },
      {
        heading: "5. Data Accuracy and Updates",
        points: [
          "Service providers are responsible for ensuring that profile details, service information, and contact information remain accurate and up to date.",
          "We may update profile information when changes are submitted through the platform or through authorized account actions.",
        ],
      },
      {
        heading: "6. Policy Updates",
        points: [
          "We may update this Privacy Policy from time to time. Continued use of the platform after changes means the updated policy will apply.",
        ],
      },
    ],
  },
  hi: {
    title: "गोपनीयता नीति",
    updated: "अंतिम अपडेट: 31 मार्च 2026",
    intro:
      "यह गोपनीयता नीति बताती है कि Bhada24 प्लेटफॉर्म पर सेवा प्रदाताओं की जानकारी को कैसे संभालता है। हमारा प्लेटफॉर्म सेवा प्रदाताओं को अपनी सेवाएं सूचीबद्ध करने, अधिक ग्राहकों तक पहुंचने और अपना व्यवसाय बढ़ाने में मदद करने के लिए बनाया गया है।",
    sections: [
      {
        heading: "1. हम कौन सी जानकारी लेते हैं",
        points: [
          "हम सेवा प्रदाताओं से नाम, मोबाइल नंबर, ईमेल, प्रोफाइल फोटो, व्यवसाय से जुड़ी जानकारी, सेवा लिस्टिंग और प्लेटफॉर्म पर दी गई अन्य जानकारी ले सकते हैं।",
          "हम लॉगिन, नोटिफिकेशन और प्लेटफॉर्म सुरक्षा के लिए आवश्यक तकनीकी और उपयोग संबंधी जानकारी भी ले सकते हैं।",
        ],
      },
      {
        heading: "2. जानकारी का उपयोग कैसे किया जाता है",
        points: [
          "हम सेवा प्रदाता की जानकारी का उपयोग लिस्टिंग बनाने, अकाउंट प्रबंधन, पूछताछ संभालने, प्लेटफॉर्म संचालन सुधारने और संभावित ग्राहकों से जोड़ने के लिए करते हैं।",
          "हम संपर्क विवरण का उपयोग अकाउंट संचार, ग्राहक पूछताछ, सहायता, सेवा समन्वय और व्यवसाय से संबंधित संवाद के लिए कर सकते हैं।",
        ],
      },
      {
        heading: "3. संपर्क जानकारी साझा करना",
        points: [
          "हम सेवा प्रदाता का संपर्क विवरण केवल वैध व्यवसायिक उद्देश्य के लिए उपयोगकर्ताओं के साथ साझा कर सकते हैं, जैसे सूचीबद्ध सेवाओं, बुकिंग, उपलब्धता, मूल्य या संबंधित व्यवसायिक चर्चा के लिए संपर्क करना।",
          "सेवा प्रदाता की संपर्क जानकारी का उपयोग असंबंधित मार्केटिंग, स्पैम या गैर-व्यवसायिक उद्देश्य के लिए नहीं किया जाना चाहिए।",
        ],
      },
      {
        heading: "4. डेटा सुरक्षा",
        points: [
          "हम प्लेटफॉर्म पर संग्रहित व्यक्तिगत और व्यवसायिक जानकारी की सुरक्षा के लिए उचित कदम उठाते हैं, लेकिन किसी भी ऑनलाइन प्रणाली में पूर्ण सुरक्षा की गारंटी नहीं दी जा सकती।",
          "सेवा प्रदाता अपने अकाउंट क्रेडेंशियल और डिवाइस सुरक्षा के लिए स्वयं जिम्मेदार हैं।",
        ],
      },
      {
        heading: "5. जानकारी की सहीता",
        points: [
          "सेवा प्रदाता यह सुनिश्चित करने के लिए जिम्मेदार हैं कि उनका प्रोफाइल, सेवा विवरण और संपर्क जानकारी सही और अद्यतन रहे।",
          "प्लेटफॉर्म पर अधिकृत बदलाव होने पर प्रोफाइल जानकारी अपडेट की जा सकती है।",
        ],
      },
      {
        heading: "6. नीति में बदलाव",
        points: [
          "हम समय-समय पर इस गोपनीयता नीति को अपडेट कर सकते हैं। बदलाव के बाद प्लेटफॉर्म का उपयोग जारी रखने पर अद्यतन नीति लागू होगी।",
        ],
      },
    ],
  },
};

export default function PrivacyPolicy() {
  const { i18n } = useTranslation();
  const content = i18n.language === "hi" ? policyContent.hi : policyContent.en;

  return (
    <div className={styles.pageContainer}>
      <AppHeader title={content.title} showBack />
      <div className={styles.contentWrapper}>
        <section className={styles.heroCard}>
          <h1 className={styles.pageTitle}>{content.title}</h1>
          <p className={styles.updatedAt}>{content.updated}</p>
          <p className={styles.introText}>{content.intro}</p>
        </section>

        <section className={styles.legalCard}>
          {content.sections.map((section) => (
            <div key={section.heading} className={styles.sectionBlock}>
              <h2 className={styles.sectionTitle}>{section.heading}</h2>
              {section.points.map((point) => (
                <p key={point} className={styles.sectionText}>{point}</p>
              ))}
            </div>
          ))}
        </section>
      </div>
    </div>
  );
}